// eslint-disable-next-line max-classes-per-file
import { removeItem } from './array-utils';

type SignalCallback<T> = (arg: T) => void;
type SignalFilter<T> = (arg: T) => boolean;

export class Signal<T = void> {
  public active: boolean = true;
  public memorize: boolean = false;

  private readonly bindings: SignalBinding<T>[] = [];
  private readonly prevParameters: T[] = [];
  private lastDispatchArg: T;
  private dispatchCounter: number = 0;

  public hasListeners(): boolean {
    return this.bindings.length > 0;
  }

  public dispatch(arg: T): void {
    if (!this.active) return;
    if (this.memorize) this.prevParameters.push(arg);
    this.lastDispatchArg = arg;
    this.dispatchCounter += 1;
    this.bindings.slice().forEach((b) => b.execute(arg));
  }

  public add(
    callback: SignalCallback<T>,
    filter?: SignalFilter<T>,
    dispatchLastArg: boolean = false,
  ): SignalBinding<T> {
    const binding = this.addBinding(new SignalBinding(this, callback, filter, false));
    if (dispatchLastArg && this.dispatchCounter > 0) binding.execute(this.lastDispatchArg);
    return binding;
  }

  public addOnce(callback: SignalCallback<T>, filter?: SignalFilter<T>): SignalBinding<T> {
    return this.addBinding(new SignalBinding(this, callback, filter, true));
  }

  private addBinding(binding: SignalBinding<T>): SignalBinding<T> {
    this.bindings.push(binding);
    if (this.memorize) this.prevParameters.forEach((arg) => binding.execute(arg));
    return binding;
  }

  public remove(binding: SignalBinding<T>): void {
    removeItem(this.bindings, binding);
    binding.destroy();
  }

  public removeAll(): void {
    this.bindings.slice().forEach((binding) => binding.destroy());
    this.bindings.length = 0;
  }
}

export class SignalBinding<T> {
  public active: boolean = true;

  private readonly once: boolean = false;
  private callback: ((arg: T) => void) | null;
  private filter: ((arg: T) => boolean) | null;
  private signal: Signal<T> | null;

  constructor(
    signal: Signal<T>,
    callback: (arg: T) => void,
    filter: ((arg: T) => boolean) | null = null,
    once: boolean = false,
  ) {
    this.signal = signal;
    this.callback = callback;
    this.filter = filter;
    this.once = once;
  }

  public execute(arg: T): void {
    if (!this.signal || !this.callback || !this.active) return;
    if (this.filter && !this.filter(arg)) return;
    const cb = this.callback;
    cb(arg);

    if (this.once) this.detach();
  }

  public detach(): void {
    if (this.signal) this.signal.remove(this);
  }

  public destroy(): void {
    this.signal = null;
    this.callback = null;
    this.filter = null;
  }
}
